import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { blogData } from '../data/extraMockData';
import { blogPostsArray } from '../data/blogPosts';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Combine long-form SEO posts (with slug) + short intro-style posts
const allPosts = [
  ...blogPostsArray.map((p) => ({
    id: p.slug,
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    date: p.date,
    category: p.category,
    image: p.image,
    imageAlt: p.imageAlt,
    readTime: p.readTime,
    full: true,
  })),
  ...blogData.map((b) => ({
    ...b,
    id: `legacy-${b.id}`,
    full: false,
    imageAlt: b.title,
  })),
];

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const categories = ['all', ...new Set(allPosts.map((b) => b.category))];
  const filtered =
    selectedCategory === 'all'
      ? allPosts
      : allPosts.filter((b) => b.category === selectedCategory);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  const PostCard = ({ post, featuredLayout = false }) => {
    const Wrapper = post.full ? Link : 'div';
    const wrapperProps = post.full ? { to: `/blog/${post.slug}` } : {};

    if (featuredLayout) {
      return (
        <Wrapper
          {...wrapperProps}
          className={`grid lg:grid-cols-2 gap-6 md:gap-8 mb-10 md:mb-12 group ${
            post.full ? 'cursor-pointer' : ''
          }`}
        >
          <div className="rounded-2xl overflow-hidden h-[240px] md:h-[350px]">
            <img
              src={post.image}
              alt={post.imageAlt}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-[#00897B]/10 text-[#00897B] text-xs font-medium rounded-full">
                {post.category}
              </span>
              <span className="text-gray-500 text-xs md:text-sm flex items-center gap-1">
                <Calendar size={13} />
                {new Date(post.date).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight group-hover:text-[#00897B] transition-colors">
              {post.title}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-5 md:mb-6 text-sm md:text-base">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-xs md:text-sm text-gray-500 flex items-center gap-1">
                <Clock size={14} />
                {post.readTime} leestijd
              </span>
              {post.full && (
                <span className="text-[#00897B] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                  Lees meer <ArrowRight size={14} />
                </span>
              )}
            </div>
          </div>
        </Wrapper>
      );
    }

    return (
      <Wrapper
        {...wrapperProps}
        className={`bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-md transition-shadow group h-full flex flex-col ${
          post.full ? 'cursor-pointer' : ''
        }`}
      >
        <div className="h-44 md:h-48 overflow-hidden">
          <img
            src={post.image}
            alt={post.imageAlt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="p-5 md:p-6 flex-1 flex flex-col">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-2 py-0.5 bg-[#00897B]/10 text-[#00897B] text-xs font-medium rounded-full">
              {post.category}
            </span>
            <span className="text-gray-400 text-xs">
              {new Date(post.date).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'short',
              })}
            </span>
          </div>
          <h3 className="font-bold text-gray-900 group-hover:text-[#00897B] transition-colors mb-2 leading-snug">
            {post.title}
          </h3>
          <p className="text-gray-600 text-sm line-clamp-2">{post.excerpt}</p>
          <div className="mt-4 flex items-center justify-between pt-3 border-t border-gray-50">
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock size={12} />
              {post.readTime}
            </span>
            {post.full && (
              <ArrowRight
                size={14}
                className="text-gray-400 group-hover:text-[#00897B] group-hover:translate-x-1 transition-all"
              />
            )}
          </div>
        </div>
      </Wrapper>
    );
  };

  return (
    <main className="pt-28 min-h-screen">
      <SEO
        title="Blog & Kennisbank | Staffing Force – Finance, HR & Duurzaamheid"
        description="Praktische gidsen en actueel nieuws over interim Finance, HR Business Partner detachering, CSRD consulting en de arbeidsmarkt in Nederland."
      />

      <PageHero
        label="Actueel"
        title="Blog & Kennisbank"
        description="Het laatste nieuws, praktische gidsen en inzichten over Finance, HR en duurzaamheid in Nederland."
        image="https://images.unsplash.com/photo-1495020689067-958852a7765e?w=1920&q=85&auto=format&fit=crop"
      />

      <section className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16">
        {/* Category filter */}
        <FadeIn>
          <div className="flex flex-wrap gap-2 mb-10 md:mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedCategory(c)}
                className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all min-h-[40px] ${
                  selectedCategory === c
                    ? 'bg-[#00897B] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {c === 'all' ? 'Alles' : c}
              </button>
            ))}
          </div>
        </FadeIn>

        {featured && (
          <FadeIn>
            <PostCard post={featured} featuredLayout />
          </FadeIn>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((post, i) => (
            <FadeIn key={post.id} delay={i * 0.06}>
              <PostCard post={post} />
            </FadeIn>
          ))}
        </div>
      </section>
    </main>
  );
};

export default BlogPage;
